import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Warning, WarningCircle, Clock, DotOutline, PencilSimple } from "@phosphor-icons/react";

// Compact 12-month compliance-calendar grid (matches the workbook Brief layout).
// One row per tracker item, 12 cells (Jan..Dec). State colours mirror the
// Brief CF palette in precedence order:
//   overdue   → red (deadline in the past)
//   due       → bright green (deadline this month)
//   soon      → amber (deadline within 14 days but different month than today)
//   upcoming  → royal blue (deadline further out — was mint, changed for readability)
//   done      → dark slate (completion landed in this month)
//   idle      → soft neutral

const STATE_STYLE = {
  idle:     { bg: "bg-neutral-100 dark:bg-neutral-800/60",                border: "border-neutral-200 dark:border-neutral-700/60" },
  done:     { bg: "bg-slate-800 dark:bg-slate-200",                       border: "border-slate-900 dark:border-slate-100" },
  upcoming: { bg: "bg-blue-600 dark:bg-blue-500",                         border: "border-blue-700 dark:border-blue-600" },
  soon:     { bg: "bg-amber-300 dark:bg-amber-400",                       border: "border-amber-500 dark:border-amber-600" },
  due:      { bg: "bg-emerald-400 dark:bg-emerald-400",                   border: "border-emerald-600 dark:border-emerald-500" },
  overdue:  { bg: "bg-red-500 dark:bg-red-500",                           border: "border-red-700 dark:border-red-700" },
};

const STATE_LABEL = { idle: "No activity", done: "Completed", upcoming: "Upcoming", soon: "Due soon", due: "Due this month", overdue: "Overdue" };

// State precedence (highest wins) — matches CF rule ordering in the workbook.
const STATE_RANK = { overdue: 5, due: 4, soon: 3, upcoming: 2, done: 1, idle: 0 };
const rankOf = (s) => STATE_RANK[s] ?? 0;

// Fold 52 ISO-week cells into 12 month cells using each cell's `date` field.
// The strongest state (per STATE_RANK) wins for the month, matching the
// stopIfTrue precedence of the workbook's conditional-formatting stack.
function weeksToMonths(weeks) {
  const months = Array.from({ length: 12 }, (_, i) => ({ month: i + 1, state: "idle", date: null }));
  (weeks || []).forEach((c) => {
    if (!c || !c.date) return;
    const m = parseInt(String(c.date).slice(5, 7), 10);
    if (!(m >= 1 && m <= 12)) return;
    if (rankOf(c.state) > rankOf(months[m - 1].state)) {
      months[m - 1].state = c.state;
      months[m - 1].date = c.date;
    }
  });
  return months;
}

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function MonthCell({ cell }) {
  const s = STATE_STYLE[cell.state] || STATE_STYLE.idle;
  const title = cell.date
    ? `${MONTH_LABELS[cell.month - 1]}: ${STATE_LABEL[cell.state]} — ${cell.date}`
    : `${MONTH_LABELS[cell.month - 1]}: ${STATE_LABEL[cell.state]}`;
  return (
    <div
      title={title}
      data-testid={`month-cell-${cell.month}-${cell.state}`}
      className={`inline-block w-6 h-4 border ${s.bg} ${s.border} shrink-0`}
    />
  );
}

function Legend() {
  return (
    <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mb-3" data-testid="calendar-legend">
      {["done", "upcoming", "soon", "due", "overdue", "idle"].map((k) => (
        <div key={k} className="flex items-center gap-1.5">
          <span className={`inline-block w-3 h-3 border ${STATE_STYLE[k].bg} ${STATE_STYLE[k].border}`} />
          <span>{STATE_LABEL[k]}</span>
        </div>
      ))}
    </div>
  );
}

function CalendarRow({ item, weeks, onSave }) {
  const [editSme, setEditSme] = useState(false);
  const [editLast, setEditLast] = useState(false);
  const [smeValue, setSmeValue] = useState(item.sme || "");
  const [lastValue, setLastValue] = useState(item.last_completed || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSmeValue(item.sme || ""); }, [item.sme]);
  useEffect(() => { setLastValue(item.last_completed || ""); }, [item.last_completed]);

  const statIcons = {
    overdue: <WarningCircle size={14} weight="fill" className="text-red-500" />,
    due_soon: <Warning size={14} weight="fill" className="text-amber-500" />,
    upcoming: <Clock size={14} className="text-emerald-600" />,
  };

  const saveSme = async () => {
    const val = smeValue.trim();
    if (val === (item.sme || "")) { setEditSme(false); return; }
    setBusy(true);
    try {
      await api.patch(`/tracker/${item.id}`, { sme: val });
      toast.success("SME updated");
      setEditSme(false);
      onSave && await onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update SME");
    } finally { setBusy(false); }
  };

  const saveLast = async () => {
    const val = (lastValue || "").trim() || null;
    if (val === (item.last_completed || null)) { setEditLast(false); return; }
    setBusy(true);
    try {
      // /tracker/{id}/complete pushes a completion row into history and recomputes next_due.
      await api.post(`/tracker/${item.id}/complete`, { completed_at: val });
      toast.success("Last done updated — next due recalculated");
      setEditLast(false);
      onSave && await onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update Last Done");
    } finally { setBusy(false); }
  };

  return (
    <tr className="border-b border-border/60 align-middle" data-testid={`calendar-row-${item.id}`}>
      <td className="px-3 py-2 whitespace-nowrap font-mono font-semibold text-xs">
        <button type="button"
                title={`${item.standard_title || item.standard_id} — click to copy code`}
                data-testid={`calendar-std-${item.id}`}
                onClick={() => {
                  const code = [item.standard_id, item.req_number, item.subpart ? `.${item.subpart}` : ""].filter(Boolean).join(" ").replace(" .", ".");
                  navigator.clipboard?.writeText(code);
                  toast.success(`Copied ${code}`);
                }}
                className="cursor-pointer border-b border-dotted border-muted-foreground/40 hover:text-primary hover:border-primary/60 bg-transparent p-0 font-mono font-semibold">
          {item.standard_id}
        </button>
      </td>
      <td className="px-2 py-2 whitespace-nowrap font-mono text-xs">
        {(() => {
          const code = [item.standard_id, item.req_number, item.subpart ? `.${item.subpart}` : ""].filter(Boolean).join(" ").replace(" .", ".");
          const label = `${item.req_number || "—"}${item.subpart ? `.${item.subpart}` : ""}`;
          const title = item.description ? `${item.description}\n\n(click to copy ${code})` : `Click to copy ${code}`;
          return item.req_number ? (
            <button type="button"
                    title={title}
                    data-testid={`calendar-req-${item.id}`}
                    onClick={() => { navigator.clipboard?.writeText(code); toast.success(`Copied ${code}`); }}
                    className="cursor-pointer border-b border-dotted border-muted-foreground/40 hover:text-primary hover:border-primary/60 bg-transparent p-0 font-mono">
              {label}
            </button>
          ) : label;
        })()}
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-xs group">
        {editSme ? (
          <input autoFocus type="text" value={smeValue}
            data-testid={`calendar-sme-input-${item.id}`}
            onChange={(e) => setSmeValue(e.target.value)}
            onBlur={saveSme}
            onKeyDown={(e) => { if (e.key === "Enter") saveSme(); if (e.key === "Escape") { setSmeValue(item.sme || ""); setEditSme(false); } }}
            disabled={busy}
            className="px-1 py-0.5 border border-primary/60 text-xs bg-amber-50 dark:bg-amber-950/40 w-40" />
        ) : (
          <button
            data-testid={`calendar-sme-edit-${item.id}`}
            onClick={() => setEditSme(true)}
            className="inline-flex items-center gap-1.5 hover:bg-amber-50 dark:hover:bg-amber-950/30 px-1 py-0.5 -mx-1 -my-0.5 border border-transparent hover:border-amber-300 group">
            {statIcons[item.status] || <DotOutline size={14} />}
            <span>{item.sme || <span className="text-muted-foreground italic">assign…</span>}</span>
            <PencilSimple size={11} className="opacity-0 group-hover:opacity-60" />
          </button>
        )}
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-xs">
        {editLast ? (
          <input autoFocus type="date" value={lastValue || ""}
            data-testid={`calendar-lastdone-input-${item.id}`}
            onChange={(e) => setLastValue(e.target.value)}
            onBlur={saveLast}
            onKeyDown={(e) => { if (e.key === "Enter") saveLast(); if (e.key === "Escape") { setLastValue(item.last_completed || ""); setEditLast(false); } }}
            disabled={busy}
            className="px-1 py-0.5 border border-primary/60 text-xs bg-amber-50 dark:bg-amber-950/40" />
        ) : (
          <button
            data-testid={`calendar-lastdone-edit-${item.id}`}
            onClick={() => setEditLast(true)}
            className="inline-flex items-center gap-1.5 hover:bg-amber-50 dark:hover:bg-amber-950/30 px-1 py-0.5 -mx-1 -my-0.5 border border-transparent hover:border-amber-300 group">
            <span>{item.last_completed || <span className="text-muted-foreground italic">log…</span>}</span>
            <PencilSimple size={11} className="opacity-0 group-hover:opacity-60" />
          </button>
        )}
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-xs">
        {item.next_due
          ? <span className={item.status === "overdue" ? "text-red-600 font-semibold" : ""}>{item.next_due}</span>
          : "—"}
      </td>
      <td className="px-3 py-1.5">
        <div className="flex items-center gap-0.5 flex-nowrap" data-testid={`month-strip-${item.id}`}>
          {weeksToMonths(weeks).map((c) => <MonthCell key={c.month} cell={c} />)}
        </div>
      </td>
    </tr>
  );
}

function MonthAxis() {
  return (
    <div className="flex items-center gap-0.5 select-none" data-testid="calendar-month-axis">
      {MONTH_LABELS.map((m) => (
        <div key={m} className="w-6 text-center text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
          {m}
        </div>
      ))}
    </div>
  );
}

export default function TrackerCalendar({ track, title }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    return api.get(`/tracker/calendar?track=${track}&year=${year}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/tracker/calendar?track=${track}&year=${year}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [track, year]);

  const totals = useMemo(() => {
    if (!data?.items) return { overdue: 0, due_soon: 0, upcoming: 0 };
    return data.items.reduce((acc, it) => {
      if (it.status === "overdue") acc.overdue += 1;
      else if (it.status === "due_soon") acc.due_soon += 1;
      else acc.upcoming += 1;
      return acc;
    }, { overdue: 0, due_soon: 0, upcoming: 0 });
  }, [data]);

  if (loading && !data) {
    return <div className="text-sm text-muted-foreground p-8" data-testid="calendar-loading">Loading calendar…</div>;
  }
  if (!data?.items?.length) {
    return <div className="text-sm text-muted-foreground p-8" data-testid="calendar-empty">No {title} items yet.</div>;
  }

  const yearN = data.year;
  const yearN1 = data.next_year;

  const renderYearPane = (isNext) => {
    const weeksField = isNext ? "weeks_next" : "weeks_current";
    return (
      <div className="border border-border bg-card overflow-x-auto" data-testid={`calendar-pane-${isNext ? "next" : "current"}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary/40 text-left">
              <th className="px-3 py-2 font-semibold text-xs uppercase tracking-wider">Standard</th>
              <th className="px-2 py-2 font-semibold text-xs uppercase tracking-wider">Req</th>
              <th className="px-2 py-2 font-semibold text-xs uppercase tracking-wider">SME</th>
              <th className="px-2 py-2 font-semibold text-xs uppercase tracking-wider">Last done</th>
              <th className="px-2 py-2 font-semibold text-xs uppercase tracking-wider">Next due</th>
              <th className="px-3 py-2">
                <MonthAxis />
              </th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => (
              <CalendarRow key={it.id} item={it} weeks={it[weeksField]} onSave={load} />
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="tracker-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Legend />
        <div className="flex items-center gap-3 text-xs">
          <span data-testid="calendar-totals-overdue" className="font-semibold text-red-600">{totals.overdue} overdue</span>
          <span data-testid="calendar-totals-due-soon" className="font-semibold text-amber-600">{totals.due_soon} due soon</span>
          <span data-testid="calendar-totals-upcoming" className="text-muted-foreground">{totals.upcoming} upcoming</span>
          <span className="border-l border-border pl-3">
            <button data-testid="calendar-year-prev" onClick={() => setYear((y) => y - 1)}
              className="px-2 py-0.5 border border-border hover:bg-secondary">&larr;</button>
            <span className="mx-2 font-mono font-semibold">{year} / {year + 1}</span>
            <button data-testid="calendar-year-next" onClick={() => setYear((y) => y + 1)}
              className="px-2 py-0.5 border border-border hover:bg-secondary">&rarr;</button>
          </span>
        </div>
      </div>

      <Tabs defaultValue="current">
        <TabsList data-testid="calendar-tabs">
          <TabsTrigger value="current" data-testid="calendar-tab-current">{yearN}</TabsTrigger>
          <TabsTrigger value="next" data-testid="calendar-tab-next">{yearN1}</TabsTrigger>
        </TabsList>
        <TabsContent value="current" className="mt-2">{renderYearPane(false)}</TabsContent>
        <TabsContent value="next" className="mt-2">{renderYearPane(true)}</TabsContent>
      </Tabs>
    </div>
  );
}
